import { apiClient } from './client';

// ─── Auth ─────────────────────────────────────────────────────────────────────

export const authApi = {
  register: (data: { phone: string; firstName: string; lastName: string; password: string }) =>
    apiClient.post('/auth/register', data),

  verifyOtp: (data: { phone: string; code: string }) =>
    apiClient.post('/auth/verify-otp', data),

  resendOtp: (phone: string) =>
    apiClient.post('/auth/resend-otp', { phone }),

  login: (data: { phone: string; password: string }) =>
    apiClient.post<{ accessToken: string; refreshToken: string; role: string; userId: string }>(
      '/auth/login', data
    ),

  logout: (data: { refreshToken: string; accessToken: string }) =>
    apiClient.post('/auth/logout', data),

  getMe: () =>
    apiClient.get<{
      id: string;
      phone: string;
      firstName: string;
      lastName: string;
      fullName: string;
      role: string;
      createdAt: string;
    }>('/auth/me'),

  updateProfile: (data: { firstName?: string; lastName?: string }) =>
    apiClient.put<{
      id: string;
      phone: string;
      firstName: string;
      lastName: string;
      fullName: string;
      role: string;
      createdAt: string;
    }>('/auth/profile', data),
};

// ─── Products ─────────────────────────────────────────────────────────────────

export const productApi = {
  getAll: (params?: {
    type?: 'Food' | 'Market';
    categoryId?: string;
    search?: string;
    isActive?: boolean;
    page?: number;
    pageSize?: number;
  }) => apiClient.get('/products', { params }),

  getById: (id: string) => apiClient.get(`/products/${id}`),

  create: (data: unknown) => apiClient.post('/products', data),

  update: (id: string, data: unknown) => apiClient.put(`/products/${id}`, data),

  toggleActive: (id: string) => apiClient.patch(`/products/${id}/toggle-active`),

  delete: (id: string) => apiClient.delete(`/products/${id}`),
};

// ─── Categories ───────────────────────────────────────────────────────────────

export const categoryApi = {
  getAll: (type?: 'Food' | 'Market') => apiClient.get('/categories', { params: { type } }),
  create: (data: unknown) => apiClient.post('/categories', data),
  update: (id: string, data: unknown) => apiClient.put(`/categories/${id}`, data),
  delete: (id: string) => apiClient.delete(`/categories/${id}`),
};

// ─── Orders ───────────────────────────────────────────────────────────────────

export const orderApi = {
  create: (data: unknown) => apiClient.post('/orders', data),
  getMyOrders: (page = 1, pageSize = 20) =>
    apiClient.get('/orders/my-orders', { params: { page, pageSize } }),
  getAll: (params?: unknown) => apiClient.get('/orders', { params }),
  getById: (id: string) => apiClient.get(`/orders/${id}`),
  updateStatus: (id: string, data: { status: number; note?: string }) =>
    apiClient.patch(`/orders/${id}/status`, data),
  assignCourier: (id: string, courierId: string) =>
    apiClient.post(`/orders/${id}/assign-courier`, { courierId }),
  cancel: (id: string, reason?: string) =>
    apiClient.post(`/orders/${id}/cancel`, { reason }),
};

// ─── Addresses ────────────────────────────────────────────────────────────────

export const addressApi = {
  getAll: () => apiClient.get('/addresses'),
  create: (data: unknown) => apiClient.post('/addresses', data),
  update: (id: string, data: unknown) => apiClient.put(`/addresses/${id}`, data),
  delete: (id: string) => apiClient.delete(`/addresses/${id}`),
  setDefault: (id: string) => apiClient.patch(`/addresses/${id}/default`),
};

// ─── Neighborhoods ────────────────────────────────────────────────────────────

export const neighborhoodApi = {
  getAll: () => apiClient.get('/neighborhoods'),
  create: (data: unknown) => apiClient.post('/neighborhoods', data),
  update: (id: string, data: unknown) => apiClient.put(`/neighborhoods/${id}`, data),
};

// ─── Campaigns & Coupons ──────────────────────────────────────────────────────

export const campaignApi = {
  getActive: () => apiClient.get('/campaigns/active'),
  getAll: () => apiClient.get('/campaigns'),
  create: (data: unknown) => apiClient.post('/campaigns', data),
  update: (id: string, data: unknown) => apiClient.put(`/campaigns/${id}`, data),
};

export const couponApi = {
  validate: (code: string, cartTotal: number) =>
    apiClient.post('/coupons/validate', { code, cartTotal }),
  getAll: () => apiClient.get('/coupons'),
  create: (data: unknown) => apiClient.post('/coupons', data),
};

// ─── Couriers ─────────────────────────────────────────────────────────────────

export const courierApi = {
  getAll: () => apiClient.get('/couriers'),
  create: (data: unknown) => apiClient.post('/couriers', data),
  update: (id: string, data: unknown) => apiClient.put(`/couriers/${id}`, data),
  remove: (id: string) => apiClient.delete(`/couriers/${id}`),
};

// ─── Banners ──────────────────────────────────────────────────────────────────

export const bannerApi = {
  getActive: () => apiClient.get('/banners'),
  getAll: () => apiClient.get('/banners/all'),
  create: (data: unknown) => apiClient.post('/banners', data),
  update: (id: string, data: unknown) => apiClient.put(`/banners/${id}`, data),
  delete: (id: string) => apiClient.delete(`/banners/${id}`),
};

// ─── Favorites ────────────────────────────────────────────────────────────────

export const favoriteApi = {
  getAll: () => apiClient.get('/favorites'),
  add: (productId: string) => apiClient.post('/favorites', { productId }),
  remove: (productId: string) => apiClient.delete(`/favorites/${productId}`),
};

// ─── Reports ──────────────────────────────────────────────────────────────────

export const reportApi = {
  getSummary: (params?: unknown) => apiClient.get('/admin/reports/summary', { params }),
  getDailySales: (params?: unknown) => apiClient.get('/admin/reports/daily-sales', { params }),
  getTopProducts: (params?: unknown) => apiClient.get('/admin/reports/top-products', { params }),
  getTopCustomers: (params?: unknown) => apiClient.get('/admin/reports/top-customers', { params }),
  getNeighborhoodSales: (params?: unknown) => apiClient.get('/admin/reports/neighborhood-sales', { params }),
  getPaymentMethods: (params?: unknown) => apiClient.get('/admin/reports/payment-methods', { params }),
  exportDailySalesCsv: (params?: unknown) => apiClient.get('/admin/reports/export/daily-sales', { params, responseType: 'blob' }),
};

// ─── Working Hours ────────────────────────────────────────────────────────────

export const workingHoursApi = {
  status: () => apiClient.get('/working-hours/status'),
  getAll: () => apiClient.get('/working-hours'),
  update: (data: unknown) => apiClient.put('/working-hours', data),
  createClosure: (data: unknown) => apiClient.post('/working-hours/closure', data),
  removeClosure: () => apiClient.delete('/working-hours/closure'),
};
